// Editorial primitive: destructive-action block at the bottom of admin/edit pages.
// Sibling to `components/layout/action-bar.tsx`; documented in apps/dashboard/CLAUDE.md
// under "Editorial primitives".

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Kicker } from "@/components/ui/kicker";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export interface DangerZoneProps {
  /** Title — describes the action ("Delete scenario"). */
  title: string;
  /** Muted description beneath the title. One or two sentences. */
  description: string;
  /** Label for the destructive trigger button ("Delete scenario"). */
  actionLabel: string;
  /** Runs when the user confirms inside the AlertDialog. */
  onConfirm: () => void | Promise<void>;
  /** Optional AlertDialog title. Defaults to `title`. */
  confirmTitle?: string;
  /** Optional AlertDialog description. */
  confirmDescription?: string;
  /** When true, disables the `Confirm` button inside the dialog. */
  isPending?: boolean;
}

export function DangerZone({
  title,
  description,
  actionLabel,
  onConfirm,
  confirmTitle,
  confirmDescription,
  isPending = false,
}: DangerZoneProps) {
  const [open, setOpen] = useState(false);

  const handleConfirm = async () => {
    try {
      await onConfirm();
      setOpen(false);
    } catch {
      // Caller owns error display via their mutation's onError. Leave the
      // dialog open so the user can retry without re-opening.
    }
  };

  return (
    <section
      data-slot="danger-zone"
      className="mt-8 border-t border-border/60 pt-10"
      aria-label="Danger zone"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-4">
        <div className="space-y-1">
          <Kicker>Danger zone</Kicker>
          <h2 className="text-base font-semibold">{title}</h2>
          <p className="max-w-[60ch] text-sm text-muted-foreground">
            {description}
          </p>
        </div>
        <Button
          variant="destructive"
          onClick={() => setOpen(true)}
          disabled={isPending}
        >
          {actionLabel}
        </Button>
      </div>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmTitle ?? title}</AlertDialogTitle>
            {confirmDescription && (
              <AlertDialogDescription>
                {confirmDescription}
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={(e) => {
                e.preventDefault();
                void handleConfirm();
              }}
              disabled={isPending}
            >
              {isPending ? "Working…" : actionLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
